import type { DroppedKeyPile, GameState } from './types';
import { appendEvent } from './event-log';
import { modifyKeys } from './economy';
import { mulberry32 } from './dice';
import { isCursedRelicLockedOut } from './cursed-relic-lockout';

export interface DroppedKeyPickupAllocation {
  playerId: string;
  amount: number;
}

export function normalizeDroppedKeyPiles(
  value: number | readonly DroppedKeyPile[] | undefined,
): DroppedKeyPile[] {
  if (typeof value === 'number') {
    return value > 0 ? [{ ownerPlayerId: null, amount: value }] : [];
  }
  if (value == null) return [];
  const totals = new Map<string | null, number>();
  for (const pile of value) {
    if (!Number.isFinite(pile.amount) || pile.amount <= 0) continue;
    totals.set(pile.ownerPlayerId, (totals.get(pile.ownerPlayerId) ?? 0) + pile.amount);
  }
  return Array.from(totals.entries()).map(([ownerPlayerId, amount]) => ({
    ownerPlayerId,
    amount,
  }));
}

export function addDroppedKeys(
  state: GameState,
  nodeId: string,
  amount: number,
  ownerPlayerId: string | null,
): void {
  if (amount <= 0) return;
  const piles = normalizeDroppedKeyPiles(state.droppedKeys.get(nodeId));
  const existing = piles.find((pile) => pile.ownerPlayerId === ownerPlayerId);
  if (existing != null) {
    existing.amount += amount;
  } else {
    piles.push({ ownerPlayerId, amount });
  }
  state.droppedKeys.set(nodeId, piles);
}

export function getDroppedKeyTotal(piles: number | readonly DroppedKeyPile[] | undefined): number {
  if (typeof piles === 'number') return Math.max(0, piles);
  if (piles == null) return 0;
  return piles.reduce((total, pile) => total + Math.max(0, pile.amount), 0);
}

export function getDroppedKeyTotalAt(state: GameState, nodeId: string): number {
  return getDroppedKeyTotal(state.droppedKeys.get(nodeId));
}

export function collectDroppedKeysForPlayer(
  state: GameState,
  nodeId: string,
  playerId: string,
): number {
  return (
    resolveDroppedKeyPickupAtNode(state, nodeId).find(
      (allocation) => allocation.playerId === playerId,
    )?.amount ?? 0
  );
}

function occupantOrder(state: GameState, nodeId: string): string[] {
  const insertionIndex = new Map<string, number>();
  let index = 0;
  for (const playerId of state.players.keys()) {
    insertionIndex.set(playerId, index);
    index++;
  }

  const turnIndex = new Map<string, number>();
  state.turnOrder.forEach((playerId, order) => turnIndex.set(playerId, order));

  return Array.from(state.players.values())
    .filter((player) => player.nodeId === nodeId && player.isDead !== true)
    .sort((a, b) => {
      const aTurn = turnIndex.get(a.id) ?? Number.MAX_SAFE_INTEGER;
      const bTurn = turnIndex.get(b.id) ?? Number.MAX_SAFE_INTEGER;
      if (aTurn !== bTurn) return aTurn - bTurn;
      return (insertionIndex.get(a.id) ?? 0) - (insertionIndex.get(b.id) ?? 0);
    })
    .map((player) => player.id);
}

function addAllocation(allocations: Map<string, number>, playerId: string, amount: number): void {
  if (amount <= 0) return;
  allocations.set(playerId, (allocations.get(playerId) ?? 0) + amount);
}

function allocatePile(
  state: GameState,
  pile: DroppedKeyPile,
  occupants: readonly string[],
  allocations: Map<string, number>,
): boolean {
  const eligible = occupants.filter(
    (playerId) => pile.ownerPlayerId !== playerId && !isCursedRelicLockedOut(state, playerId),
  );
  if (eligible.length === 0) return false;

  const baseShare = Math.floor(pile.amount / eligible.length);
  const remainder = pile.amount % eligible.length;
  for (const playerId of eligible) addAllocation(allocations, playerId, baseShare);

  if (remainder > 0) {
    const remainderPool = [...eligible];
    for (let i = 0; i < remainder; i++) {
      const rng = mulberry32(state.rngState);
      state.rngState = rng.nextState;
      const selectedIndex = Math.floor(rng.value * remainderPool.length);
      const [selectedPlayerId] = remainderPool.splice(selectedIndex, 1);
      if (selectedPlayerId != null) addAllocation(allocations, selectedPlayerId, 1);
    }
  }

  return true;
}

/**
 * Resolve all dropped-key pickup for a node as one board-object interaction.
 * Each owned pile is split among live occupants who are allowed to collect it;
 * the pile owner never collects their own pile. Normal movement/turn callers
 * can still use pickUpDroppedKeys, which delegates to this resolver.
 */
export function resolveDroppedKeyPickupAtNode(
  state: GameState,
  nodeId: string,
): DroppedKeyPickupAllocation[] {
  const piles = normalizeDroppedKeyPiles(state.droppedKeys.get(nodeId));
  if (piles.length === 0) return [];

  const occupants = occupantOrder(state, nodeId);
  if (occupants.length === 0) return [];

  const allocations = new Map<string, number>();
  const retained: DroppedKeyPile[] = [];
  for (const pile of piles) {
    const consumed = allocatePile(state, pile, occupants, allocations);
    if (!consumed) retained.push(pile);
  }

  const normalizedRetained = normalizeDroppedKeyPiles(retained);
  if (normalizedRetained.length > 0) {
    state.droppedKeys.set(nodeId, normalizedRetained);
  } else {
    state.droppedKeys.delete(nodeId);
  }

  const remainingDroppedKeys = normalizeDroppedKeyPiles(state.droppedKeys.get(nodeId));
  const results: DroppedKeyPickupAllocation[] = [];
  for (const playerId of occupants) {
    const amount = allocations.get(playerId) ?? 0;
    if (amount <= 0) continue;
    appendEvent(state, {
      type: 'board_object_collected',
      playerId,
      nodeId,
      objectKind: 'dropped_keys',
      quantity: amount,
      remainingDroppedKeys,
    });
    modifyKeys(state, playerId, amount, 'dropped_keys_pickup');
    results.push({ playerId, amount });
  }

  return results;
}

export function clearDroppedKeyOwner(state: GameState, ownerPlayerId: string): void {
  for (const [nodeId, piles] of state.droppedKeys.entries()) {
    const normalized = normalizeDroppedKeyPiles(piles).map((pile) =>
      pile.ownerPlayerId === ownerPlayerId ? { ...pile, ownerPlayerId: null } : pile,
    );
    const merged = normalizeDroppedKeyPiles(normalized);
    if (merged.length > 0) {
      state.droppedKeys.set(nodeId, merged);
    } else {
      state.droppedKeys.delete(nodeId);
    }
  }
}
